import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { getIcon } from "@/lib/agents";
import { Users2, Sparkles, History } from "lucide-react";
import { toast } from "sonner";

export default function Council() {
  const [agents, setAgents] = useState([]);
  const [selected, setSelected] = useState({});
  const [topic, setTopic] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = () =>
    api.get("/council/history").then((r) => setHistory(r.data.sessions || [])).catch(() => {});

  useEffect(() => {
    api.get("/agents").then((r) => {
      const active = r.data.agents.filter((a) => a.status === "active");
      setAgents(active);
      // preselecciona los primeros 3 agentes disponibles del equipo
      const pre = {};
      active.slice(0, 3).forEach((a) => (pre[a.id] = true));
      setSelected(pre);
    });
    loadHistory();
  }, []);

  const chosen = agents.filter((a) => selected[a.id]);

  const deliberate = async () => {
    if (!topic.trim()) return toast.error("Escribe un tema");
    if (chosen.length === 0) return toast.error("Elige al menos un agente");
    setRunning(true);
    setResult(null);
    try {
      const { data } = await api.post("/council/deliberate", {
        topic: topic.trim(),
        agent_ids: chosen.map((c) => c.id),
      });
      setResult(data);
      loadHistory();
      toast.success(`Consejo emitido por ${data.opinions.length} agentes`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fallo del consejo");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="p-8 space-y-6" data-testid="council-page">
      <div className="border-b border-[var(--border)] pb-5">
        <div className="label-mono mb-2">DELIBERACIÓN MULTI-AGENTE</div>
        <h1 className="font-display font-black text-4xl tracking-tight">Consejo</h1>
        <p className="text-sm text-[var(--text-dim)] mt-1">
          Somete una decisión sensible a varios masters. Cada uno opina desde su ángulo antes del humano.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-6">
        {/* Left: Setup */}
        <div className="space-y-4">
          <div className="border border-[var(--border)] bg-[var(--surface)] p-5">
            <div className="label-mono mb-3">TEMA A DELIBERAR</div>
            <Textarea
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="Ej: Subimos el precio del plan Pro un 20% este trimestre"
              className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono min-h-[100px]"
              data-testid="council-topic"
            />
          </div>

          <div className="border border-[var(--border)] bg-[var(--surface)] p-5">
            <div className="label-mono mb-3">MIEMBROS DEL CONSEJO</div>
            <div className="space-y-2 max-h-[300px] overflow-auto pr-1">
              {agents.map((a) => {
                const Icon = getIcon(a.icon);
                return (
                  <label
                    key={a.id}
                    className="flex items-center gap-3 py-2 px-2 border border-transparent hover:border-[var(--border)] cursor-pointer"
                  >
                    <Checkbox
                      checked={!!selected[a.id]}
                      onCheckedChange={(v) => setSelected({ ...selected, [a.id]: !!v })}
                      className="rounded-none border-[var(--border)] data-[state=checked]:bg-[var(--accent)] data-[state=checked]:border-[var(--accent)]"
                      data-testid={`council-pick-${a.id}`}
                    />
                    <Icon className="w-4 h-4" style={{ color: a.color }} />
                    <div className="flex-1">
                      <div className="text-sm font-semibold">{a.name}</div>
                      <div className="label-mono">{a.department}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          <Button
            onClick={deliberate}
            disabled={running || chosen.length === 0 || !topic.trim()}
            className="w-full rounded-none h-12 bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80 font-semibold"
            data-testid="council-run"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            {running ? "Deliberando…" : `Convocar consejo (${chosen.length})`}
          </Button>
        </div>

        {/* Right: Results */}
        <div className="space-y-3">
          {!result && !running && (
            <>
              <div className="border border-dashed border-[var(--border)] p-10 text-center flex flex-col items-center justify-center" data-testid="council-empty">
                <Users2 className="w-10 h-10 text-[var(--text-dim)] mb-4" />
                <div className="label-mono mb-2">CONSEJO EN ESPERA</div>
                <p className="text-sm text-[var(--text-dim)] max-w-md">
                  Elige el tema y qué agentes participan. Cada uno responderá desde su rol con nivel de riesgo y recomendación.
                </p>
              </div>

              {history.length > 0 && (
                <div className="border border-[var(--border)] bg-[var(--surface)]">
                  <div className="p-4 border-b border-[var(--border)] label-mono flex items-center gap-2">
                    <History className="w-3.5 h-3.5" /> DELIBERACIONES ANTERIORES
                  </div>
                  {history.map((h) => (
                    <button
                      key={h.id}
                      onClick={() => setResult(h)}
                      className="w-full text-left p-4 border-b border-[var(--border)] hover:bg-[var(--surface-2)]/60"
                      data-testid={`council-hist-${h.id}`}
                    >
                      <div className="text-sm text-white line-clamp-1">{h.topic}</div>
                      <div className="label-mono mt-1">
                        {(h.opinions || []).length} agentes ·{" "}
                        {h.created_at ? new Date(h.created_at).toLocaleString("es-ES") : ""}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {running && (
            <div className="border border-[var(--accent)] bg-[var(--surface)] p-6" data-testid="council-running">
              <div className="label-mono mb-2">DELIBERACIÓN EN CURSO</div>
              <div className="font-mono text-sm text-[var(--accent)] cursor-blink">
                Consultando a {chosen.length} agentes en paralelo
              </div>
            </div>
          )}

          {result && (
            <>
              <button
                onClick={() => setResult(null)}
                className="label-mono text-[var(--text-dim)] hover:text-[var(--accent)] flex items-center gap-2"
                data-testid="council-back"
              >
                <History className="w-3.5 h-3.5" /> ← Volver al historial
              </button>
              <div className="border border-[var(--border)] bg-[var(--surface)] p-5">
                <div className="label-mono mb-2">TEMA</div>
                <div className="text-sm text-white">{result.topic}</div>
              </div>
              {result.opinions.map((o) => {
                const ag = agents.find((a) => a.id === o.agent_id);
                const Icon = ag ? getIcon(ag.icon) : Users2;
                return (
                  <div key={o.agent_id} className="border border-[var(--border)] bg-[var(--surface)] p-5" data-testid={`council-op-${o.agent_id}`}>
                    <div className="flex items-center gap-3 mb-3">
                      <div
                        className="w-9 h-9 border flex items-center justify-center"
                        style={{ borderColor: ag?.color, color: ag?.color }}
                      >
                        <Icon className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="font-display font-bold text-base leading-tight">{o.agent_name}</div>
                        <div className="label-mono">{o.code}</div>
                      </div>
                    </div>
                    <div className="text-sm whitespace-pre-wrap text-[var(--text-dim)] leading-relaxed border-l-2 border-[var(--border)] pl-4">
                      {o.opinion}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
